/**
 * DirectoryTab Component
 *
 * Displays CyberSweep team directory by filtering users
 * with CyberSweep department or related positions.
 */

import React, { useEffect, useMemo } from 'react';
import { Users, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import { useUsers } from '../../services/queries/userQueries';
import { filterDirectoryUsers } from '../../utils/cyberSweepUserFilters';

/**
 * Format date for display
 */
const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

const DirectoryTab: React.FC = () => {
  // Use same hook as CyberSweepApp - no status filter (filter for active in component)
  const { data: allUsers = [], isLoading, error, refetch } = useUsers({});

  // Refetch users when component mounts to ensure fresh data after profile changes
  useEffect(() => {
    console.log('[DirectoryTab] Component mounted, refetching users...');
    refetch();
  }, [refetch]);

  // Filter to CyberSweep team members using centralized utility
  // Directory shows ALL CyberSweep users (active and inactive)
  const cyberSweepUsers = useMemo(
    () => filterDirectoryUsers(allUsers),
    [allUsers]
  );

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center py-8 text-red-500">
          <p>Error loading team directory.</p>
          <p className="text-sm mt-1">Please try again later.</p>
        </div>
      </div>
    );
  }

  // Empty state when no CyberSweep users found
  if (cyberSweepUsers.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Users className="h-8 w-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
          <p className="font-medium">No team members found.</p>
          <p className="text-sm mt-1">
            Users can add themselves by setting Department to "CyberSweep" in their Profile.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <h3 className="font-medium text-gray-900 dark:text-gray-100">Team Directory</h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {cyberSweepUsers.length} member{cyberSweepUsers.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            <tr>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Position</th>
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Phone</th>
              <th className="px-4 py-2 font-medium">Location</th>
              <th className="px-4 py-2 font-medium">Start Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {cyberSweepUsers.map(user => (
              <tr
                key={user.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                {/* Name */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {user.profilePicture ? (
                      <img
                        src={user.profilePicture}
                        alt={`${user.firstName} ${user.lastName}`}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <span className="text-xs font-medium text-purple-600 dark:text-purple-400">
                          {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                        </span>
                      </div>
                    )}
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {user.firstName} {user.lastName}
                    </span>
                  </div>
                </td>

                {/* Position */}
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                  {user.title || '-'}
                </td>

                {/* Email */}
                <td className="px-4 py-3">
                  <a
                    href={`mailto:${user.email}`}
                    className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    {user.email}
                  </a>
                </td>

                {/* Phone */}
                <td className="px-4 py-3">
                  {user.phone ? (
                    <a
                      href={`tel:${user.phone}`}
                      className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {user.phone}
                    </a>
                  ) : (
                    <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                  )}
                </td>

                {/* Location */}
                <td className="px-4 py-3">
                  {user.location ? (
                    <span className="inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                      <MapPin className="h-3.5 w-3.5 text-gray-400" />
                      {user.location}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                  )}
                </td>

                {/* Start Date */}
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                    <Calendar className="h-3.5 w-3.5 text-gray-400" />
                    {formatDate(user.createdDate)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DirectoryTab;
